import { push } from '@lagunovsky/redux-react-router'
import _ from 'lodash'
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects'

import { setBasketLoading } from '../../components/Basket/reducer'
import { API_LANGUAGE_MAPPING } from '../../global/i18n/constants'
import { localeSelector } from '../../global/i18n/selectors'

import Api from '../../services/api'

import { getBasketCallSaga } from 'components/Basket/saga'
import { startLoading, stopLoading } from 'components/Loader/LoaderPopup/reducer'
import { setErrorMessage, setPurpleNotification } from 'components/Windows/CentralMessagesList/reducer'
import { cinemaSlugToId } from 'global/helpers/cinemaList'
import { callApiAsync } from 'services/api/callAPIAsync'
import gtmAddTickets from 'services/gtm/gtmAddTickets'
import { getToken } from 'services/token/reducer'
import { getBasketItemByEventId } from '../../components/Basket/helpers'
import { basketDataSelector } from '../../components/Basket/selectors'
import { updateSeating } from './components/HallSeating/saga'
import { SEAT_OBJECT_KEYS } from './constants'
import { getCinemaSlug } from './helpers'
import {
    getHallInfoData,
    handleBasketConfirm,
    handleSeatClick,
    loadData,
    releaseBasketAndLoadHall,
    seatSelected,
    seatUnselected,
    setData,
    setDisableHall,
    setHallInfoData,
    setLoading,
    setModalWarning,
    setSelectedSeats,
    setUrlParamsOk,
} from './reducer'
import { originalSeatingSelect, selectedSeatsSelect } from './selectors'

function* getGqlEventData(movieId, site) {
    const API = new Api()
    try {
        const responseGql = yield call([API, 'getGqlEventData'], { movieId, site })
        const entry = responseGql?.data?.data?.data?.entry

        const posterUrl = entry?.PictureUrl ?? entry?.movie[0]?.PictureUrl ?? entry?.movieGroup[0]?.PictureUrl

        const dataGql = {
            genres: entry?.movie[0]?.CSFD[0]?.Genres,
            imageUrl: entry?.movie[0]?.CSFD[0]?.Photos[0]?.Url,
            movieSubtitles: entry?.movie[0]?.MovieSubtitles,
            posterUrl: posterUrl,
        }
        return dataGql
    } catch (error) {
        console.error(error)
    }
}

function* getGqlHallDataSaga({ payload }) {
    const { site, venueid } = payload
    const API = new Api()
    try {
        const responseGqlHall = yield call([API, 'getGqlHallDetail'], { site, venueid })
        const cinemaHallId = responseGqlHall?.data?.data?.data?.cinemaHallsEntries?.[0].id
        const responseGqlSeats = yield call([API, 'getSeats'], { site, cinemaHallId })

        yield put(setHallInfoData({ seats: responseGqlSeats?.data?.data?.data, hall: responseGqlHall.data.data.data }))
    } catch (error) {
        console.error(error)
    }
}

function* checkSelectedSeats() {
    const eventId = localStorage.getItem('eventid')

    if (_.isEmpty(eventId)) {
        yield put(replace('/error'))
        return
    }

    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? ''
    const { data, error } = yield call(callApiAsync, 'getBasketData', { language })

    if (error) {
        console.error(error)
        console.warn('checkSelectedSeats', 'Basket is either empty or the request failed')
    }
    const basketEvent = data?.items_tickets?.find((item) => item?.event?.eventid === parseInt(eventId, 10))

    if (_.isEmpty(basketEvent)) {
        // Basket is empty - do nothing
        return
    }

    // Find all seats in basket
    const seats = basketEvent?.tickets?.map((ticket) => _.pick(ticket, [...SEAT_OBJECT_KEYS]))
    // Set all seats in basket to selected
    yield put(setSelectedSeats(seats))
}

/**
 * Checks if there is basket from previous visit,
 * and if so set warning if the cinema id is changing.
 */
function* checkCinemaChange(paramsObject) {
    yield call(getBasketCallSaga)
    const basketCinemaId = yield select((state) => state?.components?.basket?.data?.cinemaid)
    if (!basketCinemaId || Number(basketCinemaId) < 0) {
        //New basket
        return
    }
    const site = paramsObject.site
    const cinemaIdfromUrl = cinemaSlugToId(site)
    if (String(basketCinemaId) !== String(cinemaIdfromUrl)) {
        console.warn('you have something in the basket and you are changing cinema ')
        yield put(setModalWarning('modalCinemaChange'))
        return 'Error'
    }
    const items_vouchers = yield select((state) => state?.components?.basket?.data?.items_vouchers)
    const gift_vouchers = items_vouchers?.filter((item) => item.using_type === 'Gift').length
    if (gift_vouchers > 0) {
        console.warn('you have gift voucher in basket')
        yield put(setModalWarning('modalBasketChange'))
        return 'Error'
    }
}

/**
 * Check url params in search
 * and set them into local storage
 */
function* handleURlParams(paramsObject) {
    if (!paramsObject?.eventid || isNaN(paramsObject?.eventid) || !paramsObject?.site || !paramsObject?.ref) {
        yield put(setUrlParamsOk(false))
        console.log('Wrong url params some param is missing or eventid is not number.')
        return 'Error'
    }
    localStorage.setItem('eventid', paramsObject.eventid)
    localStorage.setItem('referer', paramsObject.ref)
    localStorage.setItem('site', paramsObject.site)
    const cinemaId = cinemaSlugToId(paramsObject.site)
    localStorage.setItem('cinemaid', cinemaId)
    yield put(setUrlParamsOk(true))
}

function* releaseBasketAndLoadHallSaga() {
    yield put(setModalWarning(null))
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null
    const { error } = yield call(callApiAsync, 'cartRelease', { language })
    if (error) {
        yield put(getToken())
    }
    yield call(hallLoadDataCallSaga)
}

function* hallLoadDataCallSaga() {
    yield put(setModalWarning(null))
    const params = new URL(document.location).searchParams
    const paramsObject = Object.fromEntries(params)

    const oldEventid = yield select((state) => state?.pages?.hall?.movie?.data?.eventid)
    if (oldEventid && oldEventid == paramsObject.eventid) {
        yield call(checkSelectedSeats)
        return
    }

    const cinemaChangeResult = yield call(checkCinemaChange, paramsObject)

    if (cinemaChangeResult === 'Error') {
        return
    }

    const handleURlParamsResult = yield call(handleURlParams, paramsObject)
    if (handleURlParamsResult === 'Error') return

    yield put(setLoading(true))
    yield put(setSelectedSeats([]))

    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null

    const { data: eventData, error: eventError } = yield call(callApiAsync, 'getEventData', {
        eventid: paramsObject.eventid,
        language,
    })

    if (eventError) {
        console.error('getEventData', eventError)
        yield put(setErrorMessage(eventError))
        yield put(setLoading(false))
        return
    }

    const cinemaIdInLocalStorage = localStorage.getItem('cinemaid')
    if (String(eventData.cinemaid) !== cinemaIdInLocalStorage) {
        //check if local storage has same id like response
        //This error occurs if there is mismatch between event and site
        console.log(
            `Mismatch between eventid and site in the URL. Event has cinema id ${eventData.cinemaid},
            but url has site coresponding to cinema id ${cinemaIdInLocalStorage}.`
        )
        yield put(setUrlParamsOk(false))
    }

    yield put(setData(eventData))
    yield call(checkSelectedSeats)
    yield put(setLoading(false))

    localStorage.setItem('eventid', eventData?.eventid)
    localStorage.setItem('cinemaid', eventData?.cinemaid)

    const movieId = eventData?.title_groupid
    const site = getCinemaSlug(eventData?.cinemaid)
    yield put(getHallInfoData({ site, venueid: eventData?.venueid }))
    const gqlEventData = yield getGqlEventData(movieId, site)

    yield put(setData({ ...eventData, ...gqlEventData }))
}

function* nextStepCallSaga(action) {
    let nextStepOk = true
    const selectedSeats = action?.payload?.selectedSeats
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null
    if (_.isEmpty(selectedSeats)) return null

    yield put(setDisableHall(true))
    yield put(setBasketLoading(true))
    yield put(startLoading())

    // Get seats from basket (if there are any)
    const basketData = yield select(basketDataSelector)
    const event = getBasketItemByEventId(basketData)
    const basketTickets = event?.tickets

    // Get seat ticket IDs from basket
    const basketTicketIds = basketTickets?.map((ticket) => ticket?.ticketid) ?? []
    // Get seat ticket IDs from currently selected seats
    const selectedTicketIds = selectedSeats?.map((seat) => seat?.ticketid) ?? []

    // Find tickets that we want to add to basket (ignore those already in basket, find those that are selected but not in basket)
    const ticketsToAdd = selectedTicketIds?.filter((selectedTicketId) => !basketTicketIds?.includes(selectedTicketId))

    // Find tickets that we want to remove from basket (find seats that are in basket but are not currently selected)
    const ticketsToRemove = basketTicketIds?.filter((basketTicketId) => !selectedTicketIds?.includes(basketTicketId))

    // If there are any tickets to remove -> remove them
    if (!_.isEmpty(ticketsToRemove)) {
        const { error: removeTicketError } = yield call(callApiAsync, 'removeTicketFromBasket', {
            language,
            ticketid: ticketsToRemove,
        })

        if (removeTicketError) {
            yield put(setErrorMessage(removeTicketError))
            console.error(removeTicketError)
            nextStepOk = false
        }
    }

    // If there are any tickets to add -> add them
    if (!_.isEmpty(ticketsToAdd)) {
        const currency = 'CZK'
        const { data: addTicketData, error: addTicketError } = yield call(callApiAsync, 'addTicketsToBasket', {
            ticketids: ticketsToAdd,
            currency,
        })
        if (addTicketError) {
            yield put(setErrorMessage(addTicketError))
            console.error(addTicketError)
            nextStepOk = false
        }
        const cantAddSeat = addTicketData?.tickets_result?.some((ticket) => ticket.result !== 'OK')
        if (cantAddSeat) {
            console.warn('Some ticket was already bought by other user. ')
            yield put(setErrorMessage('TICKET_UNAVAILABLE'))
            nextStepOk = false
        } else {
            yield call(gtmAddTickets, ticketsToAdd.length)
        }
    }

    yield put(setBasketLoading(false))
    yield put(setDisableHall(false))
    yield put(stopLoading())
    yield call(getBasketCallSaga)

    // Reset hall
    yield put(setSelectedSeats([]))
    // Redirect to second step if everything succeeded
    if (nextStepOk) {
        yield put(push('/your-order'))
        return
    }
    //reload hall if there was error
    yield call(hallLoadDataCallSaga)
    yield call(updateSeating)
}

function* addSeat({ seat, selectedSeats, allSeats }) {
    // If user has selected 6 seats he cannot select another one (or if the user has selected 5 seats and he cannot select a double seat)
    if (selectedSeats?.length >= 6 || (selectedSeats?.length >= 5 && seat?.group_seatid > 0)) {
        yield put(setPurpleNotification('TOO_MANY_SEATS'))
        return
    }

    // Get seats from basket
    const basketData = yield select(basketDataSelector)
    const event = getBasketItemByEventId(basketData)
    const tickets = event?.tickets

    // Check if seat is in basket (if the seat is in basket the seat in hall seating data has no ticketid so we have to pick it from basket)
    const seatInBasket = tickets?.find((ticket) => ticket?.seatid === seat?.seatid)

    // Seat is in basket
    if (seatInBasket) {
        // Get seat information
        const basketSeat = _.pick(seatInBasket, [...SEAT_OBJECT_KEYS])

        // Select the seat
        yield put(seatSelected(basketSeat))

        // If the seat in basket has group seat - select it as well
        if (basketSeat?.group_seatid > 0) {
            const groupBasketSeat = tickets?.find(
                (ticket) =>
                    ticket?.group_seatid === basketSeat?.group_seatid && ticket?.seatid !== basketSeat?.group_seatid
            )
            yield put(seatSelected(groupBasketSeat))
        }

        return
    }

    // Seat is not in basket - that means we can just select it
    yield put(seatSelected(seat))

    // If the seat has group seat - select it as well
    if (seat?.group_seatid > 0) {
        const groupSeat = allSeats.find((s) => s?.group_seatid === seat?.group_seatid && s?.seatid !== seat?.seatid)
        yield put(seatSelected(groupSeat))
    }
}

function* seatClickCallSaga(action) {
    const seat = action?.payload?.seat
    const isSelectedSeat = action?.payload?.isSelectedSeat
    if (action.payload.seatType === 'wheelchair' && !isSelectedSeat) {
        yield put(setPurpleNotification('WHEELCHAIR'))
    }
    const selectedSeats = yield select(selectedSeatsSelect)
    const allSeats = yield select(originalSeatingSelect)

    // Seat is not selected
    if (!isSelectedSeat) {
        yield call(addSeat, { seat, selectedSeats, allSeats })
        return
    }

    // Seat is selected - unselect it
    yield put(seatUnselected(seat))

    // If seat has group seat - unselect it as well
    if (seat?.group_seatid > 0) {
        const groupSeat = allSeats.find((s) => s?.group_seatid === seat?.group_seatid && s?.seatid !== seat?.seatid)
        yield put(seatUnselected(groupSeat))
    }
}

export default function* hallSaga() {
    yield all([
        takeLatest(loadData.type, hallLoadDataCallSaga),
        takeLatest(handleBasketConfirm?.type, nextStepCallSaga),
        takeLatest(releaseBasketAndLoadHall?.type, releaseBasketAndLoadHallSaga),
        takeLatest(getHallInfoData?.type, getGqlHallDataSaga),
        takeEvery(handleSeatClick.type, seatClickCallSaga),
    ])
}
